import { useEffect, useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { History, Plus, X } from 'lucide-react';

import { fieldIssues, isConflict, useAudit, useCreateProject, usePatchProject } from '../api/planning-hooks';
import type { PatchProjectBody, ProjectFieldPatch } from '../api/planning';
import { PROJECT_PRIORITIES, PROJECT_PRIORITY_LABELS } from '../api/types';
import type { Assignment, AuditChange, AuditEntry, Project, ProjectStatus, Priority, RegistryDocument } from '../api/types';
import { formatDateTime } from '../lib/date';
import { statusOptions } from '../lib/statuses';
import { DatePicker } from './DatePicker';
import { initials, PersonPickerModal } from './PersonPickerModal';
import { Badge, Button, Field, Input, Modal, Select, Spinner, Textarea } from './ui';

interface ProjectEditModalProps {
  open: boolean;
  onClose: () => void;
  registry: RegistryDocument;
  /** null = create a new project. */
  project: Project | null;
  /**
   * Pre-bound stream for a CREATE (`project === null`) — the per-stream «+» in
   * «По потокам» opens the card already pointed at its own stream. Editing
   * always uses the project's own stream; an unset value keeps the old default
   * (first dictionary stream).
   */
  defaultStreamId?: string;
}

interface Draft {
  title: string;
  streamId: string;
  categoryId: string;
  status: ProjectStatus;
  priority: Priority;
  dueDate: string | null;
  hours: string;
  description: string;
  assignments: Assignment[];
  /** Linked systems (F-21) — the picker sends the WHOLE list (`set.systemIds`). */
  systemIds: string[];
  atRisk: boolean;
}

function draftOf(project: Project | null, registry: RegistryDocument, defaultStreamId?: string): Draft {
  if (!project) {
    return {
      title: '',
      streamId: defaultStreamId ?? registry.streams[0]?.id ?? '',
      categoryId: '',
      status: 'new',
      priority: 'medium',
      dueDate: null,
      hours: '',
      description: '',
      assignments: [],
      systemIds: [],
      atRisk: false,
    };
  }
  return {
    title: project.title,
    streamId: project.streamId,
    categoryId: project.categoryId ?? '',
    status: project.status,
    priority: project.priority,
    dueDate: project.dueDate,
    hours: project.hours === null ? '' : String(project.hours),
    description: project.description,
    assignments: project.assignments,
    systemIds: project.systemIds ?? [],
    atRisk: project.atRisk,
  };
}

/**
 * F-04: the full project card. This is where the STREAM select lives (F-15
 * keeps it out of the row), the single «Срок до» date (F-04), the category
 * from the dictionary (F-06) and the assignment editor with per-assignee
 * `personPriority` (F-11/F-17; `teamPriority` is edited in TeamsView).
 */
export function ProjectEditModal({ open, onClose, registry, project, defaultStreamId }: ProjectEditModalProps): ReactElement {
  const create = useCreateProject();
  const patch = usePatchProject();
  const [draft, setDraft] = useState<Draft>(() => draftOf(project, registry, defaultStreamId));
  const [formError, setFormError] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const editing = project !== null;

  // Re-seed the form whenever the dialog opens (or switches project). The
  // draft is intentionally re-seeded from `registry` too: the dictionary must
  // be current for the selects, and the dialog is only open when the user is
  // not mid-typing elsewhere. AFTER a 409 this same effect re-opens the form
  // on the fresh document — the pages re-resolve `project` by id from the
  // healed cache, so the user sees the state that is actually stored.
  useEffect(() => {
    if (open) {
      setDraft(draftOf(project, registry, defaultStreamId));
      setFormError(null);
    }
  }, [open, project, registry, defaultStreamId]);

  const busy = create.isPending || patch.isPending;
  const error = (create.error ?? patch.error) as Error | null;
  const conflict = isConflict(error);
  const issueOf = (field: string): string | undefined => fieldIssues(error).get(field);

  const personById = useMemo(() => new Map(registry.people.map((person) => [person.id, person])), [registry.people]);

  const set = (part: Partial<Draft>) => setDraft((current) => ({ ...current, ...part }));

  /**
   * Multi-field save with bounded 409 retry (owner feedback 2026-10-15).
   *
   * The CAS token is the document's `version`; a single multi-field edit on
   * a busy project can race with one inline row edit (at-risk, status, etc.)
   * and get a 409. The cache is healed automatically by `useRegistryMutation`
   * (the fresh `current` document replaces ours), and this same effect
   * re-seeds the form from the new doc — so the user's text isn't lost.
   * What we still have to do is RE-SEND the same diff against the new
   * version. Up to 3 attempts; after that, the form shows the warn badge
   * with the fresh state and the user clicks Save again.
   */
  const SAVE_RETRY_LIMIT = 3;
  const save = async () => {
    if (draft.title.trim() === '') {
      setFormError('Название обязательно');
      return;
    }
    const hours = draft.hours.trim() === '' ? null : Number(draft.hours.trim());
    if (hours !== null && (!Number.isFinite(hours) || hours < 0)) {
      setFormError('Трудозатраты — неотрицательное число часов');
      return;
    }
    const fields = {
      title: draft.title.trim(),
      streamId: draft.streamId,
      categoryId: draft.categoryId || null,
      status: draft.status,
      priority: draft.priority,
      dueDate: draft.dueDate,
      hours,
      atRisk: draft.atRisk,
      description: draft.description,
      systemIds: draft.systemIds,
    };

    if (!editing) {
      // A CREATE has no race with an existing row — a single attempt.
      await new Promise<void>((resolve) => {
        create.mutate(
          { version: registry.version, ...fields, assignments: draft.assignments },
          {
            onSuccess: () => {
              onClose();
              resolve();
            },
            onError: () => resolve(),
          },
        );
      });
      return;
    }

    // The PATCH diff is built against the entity THIS MODAL OPENED ON. If a
    // 409 re-opens the form on the healed document, the useEffect above
    // already swapped `project` to the fresh row, so rebuilding the diff
    // each attempt against the current `project` is correct: the diff tracks
    // the user's intent (what they typed) vs whatever is now stored, so
    // fields the user didn't change won't be re-sent.
    const buildPatchBody = (): PatchProjectBody | null => {
      const changed: ProjectFieldPatch = {};
      const original = project;
      for (const key of Object.keys(fields) as Array<keyof typeof fields>) {
        const value = fields[key];
        const previous = original[key];
        // Lists (systemIds) compare by content — a re-created array with the same
        // ids is NOT a change.
        const isList = Array.isArray(value) || Array.isArray(previous);
        const differs = isList
          ? JSON.stringify(value) !== JSON.stringify(previous)
          : value !== previous;
        if (differs) changed[key] = value as never;
      }
      const assignmentsChanged =
        draft.assignments.length !== original.assignments.length ||
        draft.assignments.some((next) => {
          const previous = original.assignments.find((a) => a.personId === next.personId);
          return !previous || previous.personPriority !== next.personPriority || previous.teamPriority !== next.teamPriority;
        });

      if (Object.keys(changed).length === 0 && !assignmentsChanged) return null;
      return {
        version: registry.version,
        set: changed,
        ...(assignmentsChanged ? { assignments: { set: draft.assignments } } : {}),
      };
    };

    for (let attempt = 0; attempt < SAVE_RETRY_LIMIT; attempt += 1) {
      const body = buildPatchBody();
      if (body === null) {
        // Nothing left to send (the healed document already matches the
        // user's intent — they typed a value someone else also wrote).
        onClose();
        return;
      }
      const outcome = await new Promise<'ok' | 'conflict' | 'error'>((resolve) => {
        patch.mutate(
          { id: project.id, body },
          {
            onSuccess: () => resolve('ok'),
            onError: (err) => {
              if (isConflict(err)) {
                // The hook has already put the fresh document into the cache;
                // the useEffect above re-seeds the form on the next render.
                resolve('conflict');
              } else {
                resolve('error');
              }
            },
          },
        );
      });
      if (outcome === 'ok') {
        onClose();
        return;
      }
      if (outcome === 'error') {
        // Surface the real error (e.g. 422 invalid_ref). The modal's footer
        // renders the existing `error`/`issueOf` block — nothing more to do.
        return;
      }
      // outcome === 'conflict': the loop retries against the healed `registry.version`.
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? 'Редактировать проект' : 'Новый проект'}
      description="Единственная дата проекта — «срок до». Поток выбирается здесь."
      footer={
        <>
          {conflict ? (
            // Non-blocking: the fresh document is already in the cache and the
            // form above has been re-seeded from it — just tell the user why
            // their edits were replaced.
            <span className="badge badge--warn" title={error?.message}>
              Изменён кем-то другим — данные обновлены
            </span>
          ) : error ? (
            <span className="badge badge--bad" role="alert" title={error.message}>
              Не сохранено
            </span>
          ) : null}
          <Button variant="secondary" size="sm" onClick={onClose} disabled={busy}>
            Отмена
          </Button>
          <Button variant="primary" size="sm" onClick={save} loading={busy}>
            Сохранить
          </Button>
        </>
      }
    >
      <div className="form-grid">
        <div className="form-grid__full">
          <Field
            label="Название"
            htmlFor="project-title"
            required
            error={formError ?? issueOf('title')}
          >
            <Input
              id="project-title"
              value={draft.title}
              aria-invalid={formError !== null || issueOf('title') !== undefined ? true : undefined}
              onChange={(event) => set({ title: event.target.value })}
            />
          </Field>
        </div>

        <Field label="Поток" htmlFor="project-stream" hint="Перенос потока — только здесь, в карточке (F-15)" error={issueOf('streamId')}>
          <Select id="project-stream" value={draft.streamId} onChange={(event) => set({ streamId: event.target.value })}>
            {registry.streams.map((stream) => (
              <option key={stream.id} value={stream.id}>
                {stream.name}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Категория" htmlFor="project-category" hint="Только из словаря (F-06)" error={issueOf('categoryId')}>
          <Select id="project-category" value={draft.categoryId} onChange={(event) => set({ categoryId: event.target.value })}>
            <option value="">— не задана —</option>
            {registry.categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </Select>
        </Field>

        {/* Options from the managed status dictionary (F-01/F-23): dictionary
            order, current labels, custom statuses included. */}
        <Field label="Статус" htmlFor="project-status" error={issueOf('status')}>
          <Select id="project-status" value={draft.status} onChange={(event) => set({ status: event.target.value })}>
            {statusOptions(registry.statuses).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Приоритет в потоке" htmlFor="project-priority" error={issueOf('priority')}>
          <Select id="project-priority" value={draft.priority} onChange={(event) => set({ priority: event.target.value as Priority })}>
            {PROJECT_PRIORITIES.map((priority) => (
              <option key={priority} value={priority}>
                {PROJECT_PRIORITY_LABELS[priority]}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Срок до" htmlFor="project-due" hint="Формат дд.мм.гггг; пусто — срока нет" error={issueOf('dueDate')}>
          <DatePicker
            id="project-due"
            label="Срок до"
            value={draft.dueDate}
            onChange={(iso) => set({ dueDate: iso })}
          />
        </Field>

        <Field label="Трудозатраты, часов" htmlFor="project-hours" hint="Оценка на весь проект (F-03)" error={issueOf('hours')}>
          <Input
            id="project-hours"
            type="number"
            min={0}
            step={1}
            placeholder=""
            value={draft.hours}
            onChange={(event) => set({ hours: event.target.value })}
          />
        </Field>

        <div className="form-grid__full">
          <Field label="Описание" htmlFor="project-description" hint="Переносы строк сохраняются" error={issueOf('description')}>
            <Textarea
              id="project-description"
              value={draft.description}
              onChange={(event) => set({ description: event.target.value })}
            />
          </Field>
        </div>

        <div className="form-grid__full">
          <Field
            label="Системы"
            htmlFor="project-systems"
            hint="Связанные системы каталога (F-21); сохраняется весь список целиком"
            error={issueOf('systemIds')}
          >
            <div className="check-list" id="project-systems" role="group" aria-label="Связанные системы">
              {registry.systems.length === 0 ? (
                <p className="assignment-editor__empty">В каталоге ещё нет систем.</p>
              ) : (
                registry.systems.map((system) => (
                  <label
                    key={system.id}
                    className={system.archived ? 'check-field system-row--archived' : 'check-field'}
                    htmlFor={`project-system-${system.id}`}
                  >
                    <input
                      id={`project-system-${system.id}`}
                      type="checkbox"
                      checked={draft.systemIds.includes(system.id)}
                      onChange={(event) =>
                        set({
                          systemIds: event.target.checked
                            ? [...draft.systemIds, system.id]
                            : draft.systemIds.filter((id) => id !== system.id),
                        })
                      }
                    />
                    {system.name}
                    {system.archived ? <Badge tone="warn">в архиве</Badge> : null}
                  </label>
                ))
              )}
            </div>
          </Field>
        </div>

        <div className="form-grid__full">
          <Field
            label="Исполнители"
            htmlFor="project-assignees-add"
            hint="У каждого назначения свой приоритет в личной очереди (F-17)"
            error={issueOf('assignments') ?? issueOf('assignments.personId')}
          >
            <div className="assignment-editor" id="project-assignees-add">
              {draft.assignments.length === 0 ? (
                <p className="assignment-editor__empty">Исполнители не назначены.</p>
              ) : (
                draft.assignments.map((assignment) => (
                  <div key={assignment.personId} className="assignment-editor__row">
                    <span className="assignee-chip">
                      <span className="assignee-chip__avatar" aria-hidden="true">
                        {initials(personById.get(assignment.personId)?.name ?? assignment.personId)}
                      </span>
                      <span className="assignee-chip__name">
                        {personById.get(assignment.personId)?.name ?? assignment.personId}
                      </span>
                    </span>
                    <label className="sr-only" htmlFor={`pp-${assignment.personId}`}>
                      Приоритет в личной очереди
                    </label>
                    <select
                      id={`pp-${assignment.personId}`}
                      className="input input--compact"
                      value={assignment.personPriority}
                      onChange={(event) =>
                        set({
                          assignments: draft.assignments.map((item) =>
                            item.personId === assignment.personId
                              ? { ...item, personPriority: event.target.value as Priority }
                              : item,
                          ),
                        })
                      }
                    >
                      {PROJECT_PRIORITIES.map((priority) => (
                        <option key={priority} value={priority}>
                          {PROJECT_PRIORITY_LABELS[priority]}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="icon-btn icon-btn--sm"
                      aria-label={`Убрать ${personById.get(assignment.personId)?.name ?? assignment.personId}`}
                      title="Убрать исполнителя"
                      onClick={() =>
                        set({ assignments: draft.assignments.filter((item) => item.personId !== assignment.personId) })
                      }
                    >
                      <X size={14} aria-hidden="true" />
                    </button>
                  </div>
                ))
              )}
              <Button variant="secondary" size="sm" onClick={() => setPickerOpen(true)}>
                <Plus size={14} aria-hidden="true" /> Добавить исполнителя
              </Button>
            </div>
          </Field>
        </div>
      </div>

      {editing ? <ProjectAuditHistory projectId={project.id} /> : null}

      <PersonPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        people={registry.people}
        teams={registry.teams}
        exclude={new Set(draft.assignments.map((a) => a.personId))}
        onPick={(person) =>
          set({
            assignments: [
              ...draft.assignments,
              {
                personId: person.id,
                personPriority: draft.priority,
                teamPriority: draft.priority,
                // A fresh assignment starts unordered in both manual queues.
                personPosition: null,
                teamPosition: null,
              },
            ],
          })
        }
      />
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Deferred audit history (F-20) — the project's own trail, inside the card.
// ---------------------------------------------------------------------------

/** Entries of ONE mutation share `ts` and are adjacent; `total` is the whole filter. */
interface MutationGroup {
  ts: string;
  actorName: string;
  actorEmail: string;
  action: AuditEntry['action'];
  changes: AuditChange[];
}

/** Newest first (the API's order), consecutive same-ts entries folded into one mutation. */
export function groupByMutation(entries: readonly AuditEntry[]): MutationGroup[] {
  const groups: MutationGroup[] = [];
  for (const entry of entries) {
    const last = groups[groups.length - 1];
    if (last && last.ts === entry.ts) {
      last.changes.push(...entry.changes);
      continue;
    }
    groups.push({
      ts: entry.ts,
      actorName: entry.actorName,
      actorEmail: entry.actorEmail,
      action: entry.action,
      changes: [...entry.changes],
    });
  }
  return groups;
}

const HISTORY_LIMIT = 20;

const ACTION_LABELS: Record<AuditEntry['action'], string> = {
  create: 'создание',
  update: 'изменение',
  delete: 'удаление',
  import: 'импорт',
};

/**
 * GET /api/registry/audit?entityId=<id>&limit=20 — rendered under the form of
 * an EXISTING project. Latest mutation on top; each group shows who, when and
 * the field diff. Failures are silent for editing (the form is the point) —
 * the feed itself has the /audit view.
 */
function ProjectAuditHistory({ projectId }: { projectId: string }): ReactElement {
  const audit = useAudit({ entityId: projectId, limit: HISTORY_LIMIT });
  const groups = useMemo(() => groupByMutation(audit.data?.items ?? []), [audit.data]);

  return (
    <section className="project-history" aria-label="История изменений проекта">
      <h3 className="project-history__title">
        <History size={14} aria-hidden="true" /> История изменений
      </h3>
      {audit.isPending && !audit.data ? <Spinner label="Загрузка истории" /> : null}
      {groups.length === 0 && !audit.isPending ? (
        <p className="project-history__empty">Изменений ещё не было.</p>
      ) : (
        <ul className="project-history__list">
          {groups.map((group) => (
            <li key={group.ts} className="project-history__item">
              <div className="project-history__meta">
                <span className="audit-ts">{formatDateTime(group.ts)}</span>
                <span className="project-history__actor" title={group.actorEmail}>
                  {group.actorName}
                </span>
                <Badge>{ACTION_LABELS[group.action]}</Badge>
              </div>
              {group.changes.length > 0 ? (
                <ul className="audit-changes">
                  {group.changes.map((change, index) => (
                    <li key={`${change.field}-${index}`}>
                      <span className="audit-changes__field">{change.field}</span>{' '}
                      <span className="audit-changes__from">{change.from ?? '∅'}</span> →{' '}
                      <span className="audit-changes__to">{change.to ?? '∅'}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
